import type { ViewContainerRef } from '@angular/core';
import { Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { take, of } from 'rxjs';
import { switchMap, first } from 'rxjs/operators';

import { InvitationActions } from '@core/invitations-data';
import type { ID } from '@core/ngrx-legacy';
import type { RequisitionAssignment } from '@core/requisitions-types';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';
import { TeamMembersDialogComponent } from '@recruitee/memberships-smart-ui';
import type { RequisitionAssignmentsUpdatePayload } from '@recruitee/requisitions-data';
import { RequisitionAssignmentsActions } from '@recruitee/requisitions-data';

@Injectable()
export class RequisitionsTeamMembersService {
  constructor(
    private dialogService: DialogService,
    private store: Store,
    private actions$: Actions,
  ) {}

  public editTeamMembers(
    assignments: RequisitionAssignment[],
    requisitionId: number,
    viewContainerRef: ViewContainerRef,
  ): void {
    this.dialogService.openCustom(
      TeamMembersDialogComponent,
      { width: DialogWidth.SmallX },
      {
        context: <const>'requisition',
        assignments,
        submitHandler$: this.getSubmitHandler(assignments, requisitionId).bind(this),
      },
      viewContainerRef,
    );
  }

  private getSubmitHandler(
    assignments: RequisitionAssignment[],
    requisitionId: number,
  ): (
    invitations: Array<{ email: string; roleId: number }>,
    adminIds: number[],
  ) => Observable<boolean> {
    return (invitations: Array<{ email: string; roleId: number }>, adminIds: number[]) =>
      this.submitTeamMembersHandler$(assignments, requisitionId, invitations, adminIds);
  }

  private submitTeamMembersHandler$(
    assignments: RequisitionAssignment[],
    requisitionId: number,
    invitations: Array<{ email: string; roleId: number }>,
    adminIds: number[],
  ): Observable<boolean> {
    const payload = {
      id: requisitionId,
      assignments: adminIds.map(adminId => ({
        adminId,
        id: assignments.find(assignment => assignment.adminId === adminId)?.id ?? null,
      })),
    };

    return this.teamMembersDialogHandler$(invitations, payload);
  }

  private teamMembersDialogHandler$(
    invitations: Array<{ email: string; roleId: number }>,
    payload: RequisitionAssignmentsUpdatePayload,
  ): Observable<boolean> {
    const requisitionId = payload.id;
    this.store.dispatch(RequisitionAssignmentsActions.updateOnePessimistic({ payload }));

    return this.actions$.pipe(
      ofType(RequisitionAssignmentsActions.updateOnePessimisticSuccess),
      take(1),
      switchMap(() => {
        if (invitations.length) {
          return this.sendTeamMembersInvitations$(requisitionId, invitations);
        }
        return of(true);
      }),
    );
  }

  private sendTeamMembersInvitations$(
    requisitionId: ID,
    invitations: Array<{ email: string; roleId: number }>,
  ): Observable<boolean> {
    const payload = invitations.map(invitation => ({
      ...invitation,
      requisitionIds: [requisitionId],
    }));

    this.store.dispatch(
      InvitationActions.bulkCreateInvitations({
        payload: {
          data: { invitations: payload },
          scope: `requisition:${requisitionId}`,
        },
      }),
    );

    return this.actions$
      .pipe(
        ofType(
          InvitationActions.bulkCreateInvitationsSuccess,
          InvitationActions.bulkCreateInvitationsFailed,
        ),
      )
      .pipe(
        first(),
        switchMap(({ payload }) => {
          if ('data' in payload && payload.data.uuid) {
            return of(true);
          } else {
            return of(false);
          }
        }),
      );
  }
}
